const Global = require("./Config/Global");
const ConfigurationFactory = require("./ConfigurationFactory");
const ClaimIdBasedActivation = require("./Data/ClaimIdBasedActivation");
const RemoteRequests = require("./Data/RemoteRequests");
const AnalyticsFactory = require("./AnalyticsFactory");
const DuplicateIdHandler = require("./DuplicateIdHandler")
const MachineId = require("./Machine-id");
const Logger = require("./Logger");
const ContentSentinelDispatcher = require("content-sentinel");
const Utils = require("./Utils");
const ChromeOSPwaExtensionInitialization = require("./Player/ChromeOSPwaExtensionInitialization");
const BrightSignMessaging = require("./Player/BrightSignMessaging");
const WebOSRisePlayer = require("./Player/WebOSRisePlayer");
const PlayerController = require("./Player/RisePlayerController");
const DisplayControlService = require("./Display/DisplayControlService.js");
const PlayerIntegration = require("./Player/PlayerIntegration");
const BrowserCompatibilityService = require("./BrowserCompatibilityService");
const BrowserStorageQuotaService = require("./Performance/BrowserStorageQuotaService.js");
const ContinuousPerformanceLogging = require("./Performance/ContinuousPerformanceLogging");
const MemoryUsageService = require("./Performance/MemoryUsageService");
const BrowserNetworkConnectionService = require("./BrowserNetworkConnectionService");
const CorsIssueWorkaround = require("./Issues/CorsIssueWorkaround");
const FirestickStillWatchingIssueWorkaround = require("./Issues/FirestickStillWatchingIssueWorkaround");
const QuotaExceededIssueWorkaround = require("./Issues/QuotaExceededIssueWorkaround");
const FloatingNotificationsController = require("./UI/FloatingNotificationsController");
const LowPerformanceDetection = require("./Performance/LowPerformanceDetection");
const ContentSentinelInitializationErrorUIController = require("./UI/ContentSentinelInitializationErrorUIController.js");
const WebPlayerRegistrationUIController = require("./UI/WebPlayerRegistrationUIController.js");
const WebPlayerActivationUIController = require("./UI/WebPlayerActivationUIController");

const service = {};

const PLAYER_PARAM = "player=";
const E2E_PARAM = "e2e-tests=";
const SECONDS = 1000;
const ONE_MINUTE = 60 * SECONDS;
const VIEWER_EXIT_DELAY = 10 * SECONDS;
const PRIORITY_CONTENT_TIMEOUT = ONE_MINUTE;
const QUOTA_EXCEEDED_ERROR_CODE = "E000000092";

let contentController, newContentController;

let isShowingBlack = undefined, isShowingProgressBar = true;

let priorityContentTimeout;
const DISPLAY_ID_VALIDATION_ATTEMPTS = 3;
const DISPLAY_ID_VALIDATION_RETRY_DELAY = 1000;


// Debounce for showBlackScreen: only the last value is applied after 500ms with no further calls
const SHOW_BLACK_SCREEN_DEBOUNCE_MS = 500;
let showBlackScreenDebounceTimer = null;
let pendingShowBlackScreen = null;

const _preparePlayer = () => {
  if (!ConfigurationFactory.isDisplay()) {
    if (ConfigurationFactory.isElectronPlayer() || Utils.isFedora21Browser()) {
      PlayerController.startWatchdogPing();
    }

    return;
  }

  PlayerController.setIsActive(Utils.getFromQueryString(PLAYER_PARAM));
};

const _registerServiceWorker = () => {
  Logger.viewerInfo("Registering base service worker");

  RiseVision.Viewer.ServiceWorker.register(() => {
    _load();
  });
};

const _load = () => {
  ConfigurationFactory.displayLogging();

  // check if no Display ID
  if (PlayerController.getIsActive() && ConfigurationFactory.isDisplay() && !ConfigurationFactory.isEmbed() &&
    ConfigurationFactory.DEMO_ID === ConfigurationFactory.getId()) {
    if (ConfigurationFactory.getClaimId()) {
      RiseVision.Viewer.UI.DisplayRegister.EnterClaimIdWidget.show(false);
    } else {
      RiseVision.Viewer.UI.DisplayRegister.DisplayRegisterWidget.show("DISPLAY_ID_NULL");
    }
  } else {
    RiseVision.Viewer.Messaging.init(() => {
      RiseVision.Viewer.Data.GCSController.init();
      RiseVision.Viewer.LocalMessaging.init(() => {
        service.loadPresentation();
      });
    });
  }
};

function _adjustStylesThatCauseAlignmentIssues() {
  if (ConfigurationFactory.isAndroidPlayer()) {
    const poweredByRiseDiv = window.document.getElementById("powered-by-rise-div");

    poweredByRiseDiv.style.padding = "0";
  }
}

service.checkChromeOSPwaPlayerExtension = () => {
  ConfigurationFactory.updateParameters();

  if( !ConfigurationFactory.isWebPlayer() || ConfigurationFactory.isEmbed() || ConfigurationFactory.isWebPlayerBasedRisePlayer() ) {
    return Promise.resolve(false);
  }

  const key = ConfigurationFactory.CHROME_OS_PWA_PLAYER_CONFIGURATION;

  return ChromeOSPwaExtensionInitialization.initializeAndGetConfiguration()
  .then( configuration => {
    console.log( "Chrome OS PWA Player extension found, and has been initialized" );

    window.sessionStorage.setItem( key, JSON.stringify(configuration) );

    return true;
  }).catch( error => {
    console.log( "Chrome OS PWA Player extension not found or failed.", error && error.message );

    window.sessionStorage.removeItem( key );
    return false;
  });
}

const _validateDisplayId = (displayId, remainingAttempts = DISPLAY_ID_VALIDATION_ATTEMPTS) => {
  if (!displayId) {
    return Promise.resolve();
  }

  return RemoteRequests.validateDisplayId( displayId )
  .catch(error=>{
    if (WebPlayerRegistrationUIController.isIdNotValidError(error)) {
      FloatingNotificationsController.addNotification(`Display id not valid: '${displayId}' - message: '${ error && error.message }'`, ONE_MINUTE);
      FloatingNotificationsController.showNotifications();

      try {
        localStorage.removeItem(ConfigurationFactory.WEBPLAYER_DISPLAY_ID_KEY);
        Utils.removeParamFromURL(ConfigurationFactory.getIDParamName());
      } catch(e) {}
      return;
    }

    if (remainingAttempts <= 1) {return;}

    return new Promise(resolve => setTimeout(resolve, DISPLAY_ID_VALIDATION_RETRY_DELAY))
    .then(() => _validateDisplayId(displayId, remainingAttempts - 1));
  });
};



service.configureDisplayId = () => {
  ConfigurationFactory.updateParameters();

  if (!localStorage.getItem(ConfigurationFactory.WEBPLAYER_DISPLAY_ID_KEY)) {
    PlayerIntegration.loadDisplayIdFromPlayerIfAvailable();
  }

  if (!ConfigurationFactory.isWebPlayer()) {return Promise.resolve();}
  if (ConfigurationFactory.isEmbed()) {return Promise.resolve();}

  const activationCode = ConfigurationFactory.getActivationCode();
  const displayId = ConfigurationFactory.getDisplayId();

  if (!displayId || activationCode) {
    const claimId = ConfigurationFactory.getClaimId();

    if (claimId) {
      return ClaimIdBasedActivation.generateDisplayIdFromClaimId(claimId);
    }
  }

  if (activationCode) {return Promise.resolve();}

  return _validateDisplayId(displayId);
};

service.handleContentSentinelLogMessage = logEntry => {
  RiseVision.Viewer.Logger.logTemplateEvent( logEntry );

  if( logEntry && logEntry.eventErrorCode === QUOTA_EXCEEDED_ERROR_CODE ) {
    QuotaExceededIssueWorkaround.handleQuotaExceeded();
  }
};

// This is called by Content Sentinel's dispatcher
service.reloadPageIfCorsIssueBrowserMatches = message =>
  CorsIssueWorkaround.reloadPageIfCorsIssueBrowserMatches(message);

function startContentSentinel() {
  const endpointId = ConfigurationFactory.getDisplayId() || Utils.getUniqueId();

  const timeoutPromise = ConfigurationFactory.isDisplay() ? new Promise(res=>setTimeout(res, Global.DELAY_FOR_INJECTED_SCRIPTS)) : Promise.resolve();

  return timeoutPromise
  .then(() => {
    if( !ConfigurationFactory.useContentSentinel() ) {
      return Promise.resolve({ success: true });
    }

    Logger.viewerInfo("Starting Content Sentinel", { endpointId });

    return ContentSentinelDispatcher.init({
      displayId: RiseVision.Viewer.ConfigurationFactory.getDisplayId(),
      machineId: MachineId.id(),
      heartbeatInterval: RiseVision.Viewer.Logger.heartbeatInterval(),
      heartbeatLogFn: RiseVision.Viewer.Logger.recordUptimeHeartbeat,
      logFn: service.handleContentSentinelLogMessage,
      endpointType: RiseVision.Viewer.ConfigurationFactory.endpointType(),
      endpointId,
      scheduleId: ConfigurationFactory.getScheduleId(),
      playerMessageFn: PlayerIntegration.playerMessageFn,
      duplicateIdHandlerFn: DuplicateIdHandler.handleDuplicateId,
    });
  });
}

function completeViewerServicesInitialization() {
  console.log("Completing entrypoint init");
  const endpointId = ConfigurationFactory.getDisplayId() || Utils.getUniqueId();

  Logger.viewerInfo("Starting heartbeat recording", { endpointId });
  setInterval(Logger.recordUptimeHeartbeat, Logger.heartbeatInterval());

  PlayerIntegration.subscribeToScreenPowerUpdates();

  ContinuousPerformanceLogging.start();

  const quotaFn = BrowserStorageQuotaService.checkStorageQuota;
  const networkFn = BrowserNetworkConnectionService.checkNetworkConnections;
  MemoryUsageService.logMemoryUsage();

  return quotaFn()
  .then(networkFn)
  .then(_registerServiceWorker)
  .then(() => CorsIssueWorkaround.init(endpointId))
  .then(() => Logger.viewerInfo("Viewer services initialization completed"))
  .catch((error = {}) =>{
    Logger.viewerError( "E999999999", "Unexpected error during Viewer init", JSON.stringify({ error: error.stack }) );
  });
}

service.waitUntilContentJsonExists = () => {
  if (ConfigurationFactory.isWebPlayer() && !ConfigurationFactory.isEmbed() && !Utils.getFromQueryString(E2E_PARAM)) {
    return WebPlayerActivationUIController.waitUntilContentJsonExists(service.init);
  } else {
    return Promise.resolve();
  }
}

service.init = () => {
  ConfigurationFactory.updateParameters();

  PlayerIntegration.bindPlayerUpdateButton();

  _adjustStylesThatCauseAlignmentIssues();

  if (ConfigurationFactory.needsWebPlayerDisplayId()) {
    if (ConfigurationFactory.getDisplayIdAsCancelled() || Utils.getFromQueryString(E2E_PARAM)) {
      let showActivationFn = WebPlayerActivationUIController.showWebPlayerActivationUI;

      return WebPlayerRegistrationUIController.showWebPlayerRegistrationUI(service.init, showActivationFn);
    } else {
      return WebPlayerActivationUIController.showWebPlayerActivationUI(service.init);
    }
  }

  const endpointId = ConfigurationFactory.getDisplayId() || Utils.getUniqueId();

  Logger.initEndpointLogger({
    endpointId,
    endpointType: ConfigurationFactory.endpointType(),
    endpointUrl: window.location.href,
    scheduleId: ConfigurationFactory.getScheduleId(),
    embedReferralUrl: ConfigurationFactory.isEmbedSharedSchedule() ? document.referrer : null,
    viewerVersion: Global.VIEWER_VERSION
  });

  Logger.setEndpointLoggerContentFields({ scheduleId: ConfigurationFactory.getScheduleId() });

  Logger.recordUptimeHeartbeat();

  if (ConfigurationFactory.isWebOSPlayer()) {
    WebOSRisePlayer.initWebOSRisePlayer();
  }

  Logger.reportRemoteDebugToPlayer(PlayerIntegration.getInjectedPlayer());

  PlayerIntegration.configureHeartBeatInterval();

  if (ConfigurationFactory.isBrightsignPlayer()) {
    BrightSignMessaging.init();
  }

  _logBrowserDate();
  _logWakeupSignal();
  _preparePlayer();

  if (!BrowserCompatibilityService.checkAllowedBrowser()) { return; }

  return BrowserCompatibilityService.checkBrowserCompatibility()
  .then(compatible=>{
    Logger.viewerInfo("Browser compatibility check result", { compatible });
    console.log("Browser compatibility check result", { compatible });

    if (compatible) {
      LowPerformanceDetection.start();
      FirestickStillWatchingIssueWorkaround.init();

      if (Utils.isDismissible()) {
        RiseVision.Viewer.Player.InteractionController.init();
      }

      return startContentSentinel().then(outcome => {
        Logger.viewerInfo("Content Sentinel started", { outcome });

        if( !outcome.success && ContentSentinelInitializationErrorUIController.shouldShowErrorPage( outcome.failure ) ) {
          ContentSentinelInitializationErrorUIController.show( outcome.failure );
          return;
        }

        console.log("Waiting for contentjson existence");
        return service.waitUntilContentJsonExists()
        .then(()=>{
          console.log("ContentJsonExists");
          return completeViewerServicesInitialization();
        })
      });
    }
  });
};

service.loadPresentation = () => {
  if (ConfigurationFactory.isDisplay()) {
    RiseVision.Viewer.Cache.RiseCacheController.pingCache();
  }

  const id = ConfigurationFactory.getId();

  if (id && ConfigurationFactory.isValidType()) {
    RiseVision.Viewer.Data.ViewerDataController.init(_onDataReady);

    PlayerIntegration.setDisplayId(id);
  }

  FloatingNotificationsController.showNotifications();
};

service.reloadContentController = () => {
  if (newContentController) {
    newContentController.unload();
  }

  let onContentDone = null;
  if (ConfigurationFactory.isEmbed()) {
    onContentDone = _reportDone;
  }

  newContentController = new RiseVision.Viewer.Content.ContentController(_onContentReady, onContentDone);
  newContentController.init();
}

function _logBrowserDate() {
  Logger.viewerInfo("Browser date", { browserDate: new Date().toISOString(), timestamp: Date.now() });
}

function _logWakeupSignal() {
  if( Utils.wakeupSignalHasBeenReceived() ) {
    const wakeupEventId = ConfigurationFactory.getWakeupEventId();

    Logger.viewerImportant("CLASSROOM ALERTS - Viewer has been started from a wakeup signal", wakeupEventId);
  }
}

function _onDataReady() {
  service.reloadContentController();

  if (ConfigurationFactory.isPreview() && ConfigurationFactory.getShowUi()) {
    RiseVision.Viewer.UI.ViewerPreviewWidget.show();
  }

  AnalyticsFactory.init();
}

function _onContentReady() {
  const oldContentController = contentController;

  contentController = newContentController;

  // Reset the newContentController parameter
  newContentController = null;

  if (ConfigurationFactory.isEmbed()) {
    _reportReady();
  }
  else {
    _playContent();
  }

  if (oldContentController) {
    oldContentController.unload();
  }

  AnalyticsFactory.trackLoadTime();
}

function _playContent() {
  if (isShowingProgressBar) {
    if (Utils.wakeupSignalHasBeenReceived()) {
      const wakeupEventId = ConfigurationFactory.getWakeupEventId();

      if (!contentController.checkPriorityContent()) {
        if (!priorityContentTimeout) {
          priorityContentTimeout = setTimeout(() => {
            Logger.viewerError( "E999999999", "CLASSROOM ALERTS - priority content was not received in time; closing Viewer", wakeupEventId );

            setTimeout(() => PlayerIntegration.notifyOfRegularContent(), VIEWER_EXIT_DELAY);
          }, PRIORITY_CONTENT_TIMEOUT);
        }

        Logger.logDebug('isShowingProgressBar skip playback; wait for priority content');

        return;
      } else if (priorityContentTimeout) {
        clearTimeout(priorityContentTimeout);
      }

      Logger.viewerImportant('CLASSROOM ALERTS - priority content received after wake up; proceed', wakeupEventId);
    }

    Utils.showElement("progress", false);

    isShowingProgressBar = false;
  }

  if (!contentController.play()) {
    _reportDone();
  }
}

function _applyShowBlackScreen(show) {
  if (isShowingBlack === show) {
    return;
  }
  isShowingBlack = show;

  Utils.setBackground("mainDiv", show ? "black" : "transparent");

  DisplayControlService.setDisplayControl(show);
  PlayerController.setDisplayCommand(show);
}

service.showBlackScreen = function (show) {
  const validType = ConfigurationFactory.isSharedschedule() ||
    ConfigurationFactory.isDisplay() ||
    ConfigurationFactory.isWebPlayer();

  if (!validType) { return; }

  if (isShowingBlack === show && pendingShowBlackScreen === null && showBlackScreenDebounceTimer === null) {
    return;
  }

  pendingShowBlackScreen = show;
  if (showBlackScreenDebounceTimer !== null) {
    clearTimeout(showBlackScreenDebounceTimer);
  }
  showBlackScreenDebounceTimer = setTimeout(() => {
    showBlackScreenDebounceTimer = null;

    const toApply = pendingShowBlackScreen;
    pendingShowBlackScreen = null;

    if (toApply !== undefined && toApply !== null && toApply !== isShowingBlack) {
      _applyShowBlackScreen(toApply);
    }
  }, SHOW_BLACK_SCREEN_DEBOUNCE_MS);
};

service.isShowingProgressBar = function () {
  return isShowingProgressBar;
};

function _reportReady() {
  try {
    window.embedReady();
  } catch (err) {
    Logger.logException("EntryPoint._reportReady", err);
  }
}

function _reportDone() {
  try {
    window.embedDone();
  } catch (err) {
    Logger.logException("EntryPoint._reportDone", err);
  }
}

service.embedPlay = function () {
  _playContent();
};

service.embedPause = function () {
  contentController.pause();
};

service.embedStop = function () {
  service.embedPause();
};

service.destroyContent = function() {
  if (!contentController) {
    return;
  }

  contentController.unload();
  contentController = null;
}

// This functionn is used to reset the service for unit tests
service._reset = function() {
  isShowingProgressBar = true;
  if (priorityContentTimeout) {
    clearTimeout(priorityContentTimeout);
    priorityContentTimeout = null;
  }
  if (showBlackScreenDebounceTimer) {
    clearTimeout(showBlackScreenDebounceTimer);
    showBlackScreenDebounceTimer = null;
    pendingShowBlackScreen = null;
  }
  isShowingBlack = undefined;
};

module.exports = service;
